import React, { useEffect, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { gql } from 'apollo-boost';
import { Link as RouterLink, useLocation, useHistory } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

// external dependencies
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { CircularProgress, Link, Typography } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Grid from '@material-ui/core/Grid';
import InputLabel from '@material-ui/core/InputLabel';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import Popper from '@material-ui/core/Popper';
import TextField from '@material-ui/core/TextField';

import { Autocomplete } from '@material-ui/lab';
import { ActionItem, SignUpFormTextField, SubmitButtonContainer } from '../SignUpForm.style';

import { legalUrls, phoneRegex, passWordRegex, routes } from '../../../constants.js/misc';
import { countries } from '../../../constants.js/countries';
import { signUp, logIn, saveSignUpData } from '../../../modules/auth';
import { setShowWelcomePopup } from '../../../modules/common';

import visibility from '../../../assets/icons/visibility.svg';
import visibilityOff from '../../../assets/icons/visibilityOff.svg';

//MIXPANEL
import { Mixpanel } from '../../../constants.js/mixpanel';
import ReactPixel from 'react-facebook-pixel';
import { welcomeMessageService } from '../../../services/home';
import InsightsSingUp from './InsightsSingUp/InsightsSingUp';
import { AuthFormButton, AuthFormInput, AuthFormLabel } from '../../AuthForm/AuthForm.style';
import { colors } from '../../../constants.js/colors';
import * as ga from '../../../lib/gtag';
import { detectSignupCountryDial } from '../../../utils/detectSignupCountryDial';
// ISO 3166-1 alpha-2
// ⚠️ No support for IE 11
function countryToFlag(isoCode) {
    return typeof String.fromCodePoint !== 'undefined'
        ? isoCode
              .toUpperCase()
              .replace(/./g, (char) => String.fromCodePoint(char.charCodeAt(0) + 127397))
        : isoCode;
}

const STORE_NAME_MIN_LENGTH = 4;

const hasMinTrimmedLength = (min) => (value) => (value?.trim().length ?? 0) >= min;

const fieldsSchema = yup.object({
    first_name: yup.string().trim().required('SignUp.firstNameError'),
    last_name: yup.string().trim().required('SignUp.lastNameError'),
    email: yup.string().trim().required('LogIn.emailError').email('LogIn.emailError'),
    password: yup
        .string()
        .required('LogIn.passwordErrorMinLength')
        .matches(passWordRegex, 'LogIn.passwordErrorMinLength'),
    store: yup
        .string()
        .trim()
        .required('SignUp.storeError')
        .test('store-min-length', 'SignUp.storeMinLengthError', hasMinTrimmedLength(STORE_NAME_MIN_LENGTH)),
    store_phone: yup
        .string()
        .matches(phoneRegex, 'SignUp.phoneError')
        .required('SignUp.phoneError'),
    user_phone: yup
        .string()
        .matches(phoneRegex, 'SignUp.phoneError')
        .required('SignUp.phoneError'),
});

const ONBOARDING = gql`
    {
        onboarding {
            id
            question
            onboardingOptions {
                id
                order
                option
            }
        }
    }
`;

const SignUpUserInfoForm = ({
    signUp,
    isSigningUp,
    signUpData,
    logIn,
    saveSignUpData,
    selectedPlan,
    setShowWelcomePopup,
}) => {
    const { t } = useTranslation();
    const location = useLocation();
    const { enqueueSnackbar } = useSnackbar();
    const [saving, setSaving] = useState(false);
    const [password, setPassword] = useState(false);
    const [prefix, setPrefix] = useState();

    const {
        handleSubmit,
        control,
        formState: { errors },
        watch,
        setValue,
    } = useForm({
        yupResolver: yupResolver(fieldsSchema),
        mode: 'all',
    });

    const handleSignUpError = (error) => {
        setSaving(false);
        enqueueSnackbar(t(error), {
            variant: 'error',
            autoHideDuration: 2500,
        });
    };

    const handleFormInvalid = () => {
        enqueueSnackbar(t('SignUp.formValidationError'), {
            variant: 'error',
            autoHideDuration: 3000,
        });
    };

    const fieldErrorMessage = (fieldName) => {
        const message = errors?.[fieldName]?.message;
        return message ? t(message) : '';
    };

    const handleRedirectionWelcome = () => {
        setShowWelcomePopup(true);
    };

    const showPassword = () => {
        setPassword((prevState) => !prevState);
    };

    const handleKeyDown = (e) => {
        var event = e || window.event;
        var key = event.keyCode || event.which;

        if (key === 110 || key === 190 || key === 188) {
            event.preventDefault();
        }
    };

    // Mobile keyboards skip handleKeyDown (keyCode 229), so strip the same characters on change
    const withoutPunctuation = (onChange) => (e) => onChange(e.target.value.replace(/[.,]/g, ''));

    const getCookie = (cname) => {
        var name = cname + '=';
        var ca = document.cookie.split(';');
        for (var i = 0; i < ca.length; i++) {
            var c = ca[i];
            while (c.charAt(0) === '') {
                c = c.substring(1);
            }
            if (c.indexOf(name) === 0) {
                return c.substring(name.length, c.length);
            }
        }
        return '';
    };

    const filterCountries = (options, state) => {
        return options.filter((option) => {
            return (
                option.phone.toLowerCase().includes(state.inputValue.toLowerCase()) ||
                option.label.toLowerCase().includes(state.inputValue.toLowerCase())
            );
        });
    };

    const onSubmit = (formData) => {
        setSaving(true);

        Mixpanel.track('Registro', {
            Canal: getCookie('source'),
            'Fuente de trafico': document.referrer,
            Correo: formData.email,
            'Nombre de la tienda': formData.store,
            'No whatsapp': signUpData.countryNumber + signUpData.phone,
        });

        ga.event({
            action: 'registration_complete',
            category: 'Registration',
            label: 'User completed registration',
            value: 1,
        });

        const handleRegisterOk = () => {
            ReactPixel.track('CompleteRegistration', {
                content_name: 'Tienda creada',
                store_name: formData.store,
            });

            if (location.pathname === '/sign-up/discount') {
                ReactPixel.track('AddToCart', {
                    content_name: '/sign-up/discount',
                });
            }

            // Send welcome whatsapp
            const userPhoneNumber = formData.storeCountryNumber + formData.store_phone;
            const userName = formData.first_name;
            // sendWelcomeMessage(userPhoneNumber, userName, API_URL, API_KEY);
            const data = { userPhoneNumber, userName };
            welcomeMessageService(data)
                .then((response) => console.log('Welcome mensaje', response))
                .catch((error) => console.log('Imposible enviar mensaje de bienvenida', error));
        };

        signUp(
            {
                first_name: formData.first_name,
                last_name: formData.last_name,
                email: formData.email?.trim(),
                password: formData.password,
                store: formData.store,
                phone: formData.storeCountryNumber + formData.store_phone,
                contact_phone_number: formData.countryNumber + formData.user_phone,
                source: getCookie('source'),
                verified: 1,
                user_type_id: 1,
                user_status_id: 1,
                country: signUpData.country,
                country_code: formData.storeCountry,
                on_boarding_options: [+formData.onboarding_1, +formData.onboarding_2],
                user_information: {
                    first_name: signUpData.first_name,
                    last_name: signUpData.last_name,
                    phone_number: signUpData.countryNumber + signUpData.phone,
                    business_url: formData.business_url || '',
                    country_code: signUpData.country,
                },
            },
            logIn,
            handleSignUpError,
            handleRedirectionWelcome,
            handleRegisterOk
        );

        if (location.pathname === '/sign-up/discount') {
            localStorage.setItem('campaing', 'true');
            localStorage.setItem('selectedPlan', selectedPlan);
            // if (selectedPlan === 'annual') {
            //     sendInfoWA('Hola, estos son los datos de mi tienda, quiero información sobre el plan anual.')
            // }
        } else {
            localStorage.setItem('campaing', 'false');
            // sendInfoWA('Hola, estos son los datos de mi tienda para una asesoría personalizada.')
        }
    };

    const PopperMy = function (props) {
        return <Popper {...props} style={{ width: 250 }} placement="bottom-start" />;
    };

    /** Prefill country calling codes from visitor IP (no GPS). Skips if Redux already has a country. */
    useEffect(() => {
        if (signUpData?.country) return;

        let cancelled = false;

        const run = async () => {
            const match = await detectSignupCountryDial();
            if (cancelled || !match) return;

            setValue('storeCountry', match.code);
            setValue('storeCountryNumber', match.phone);
            setValue('country', match.code);
            setValue('countryNumber', match.phone);

            saveSignUpData({
                ...signUpData,
                country: match.code,
                countryNumber: match.phone,
            });
        };

        run();

        return () => {
            cancelled = true;
        };
    }, [saveSignUpData, setValue, signUpData, signUpData?.country]);

    return (
        <>
            <form
                onSubmit={handleSubmit(onSubmit, handleFormInvalid)}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Grid container spacing={2} justifyContent="center">
                    <Grid item xs={12} lg={6}>
                        <FormControl variant="filled" fullWidth>
                            <AuthFormLabel error={!!errors?.first_name}>
                                {t('SignUp.firstName')}
                            </AuthFormLabel>
                            <Controller
                                render={({ field }) => (
                                    <AuthFormInput
                                        {...field}
                                        onChange={withoutPunctuation(field.onChange)}
                                        onKeyDown={handleKeyDown}
                                        error={!!errors?.first_name}
                                    />
                                )}
                                name="first_name"
                                control={control}
                            />
                            <FormHelperText error={!!errors?.first_name}>
                                {fieldErrorMessage('first_name')}
                            </FormHelperText>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        <FormControl variant="filled" fullWidth>
                            <AuthFormLabel error={!!errors?.last_name}>
                                {t('SignUp.lastName')}
                            </AuthFormLabel>
                            <Controller
                                render={({ field }) => (
                                    <AuthFormInput
                                        {...field}
                                        onChange={withoutPunctuation(field.onChange)}
                                        onKeyDown={handleKeyDown}
                                        error={!!errors?.last_name}
                                    />
                                )}
                                name="last_name"
                                control={control}
                            />
                            <FormHelperText error={!!errors?.last_name}>
                                {fieldErrorMessage('last_name')}
                            </FormHelperText>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        <FormControl variant="filled" fullWidth>
                            <AuthFormLabel error={!!errors?.store}>
                                {t('SignUp.nameMarket')}
                            </AuthFormLabel>
                            <Controller
                                render={({ field }) => (
                                    <AuthFormInput
                                        {...field}
                                        onKeyDown={handleKeyDown}
                                        error={!!errors?.store}
                                        placeholder={t('SignUp.nameMarketPlaceholder')}
                                    />
                                )}
                                name="store"
                                control={control}
                            />
                            <FormHelperText error={!!errors?.store}>
                                {fieldErrorMessage('store')}
                            </FormHelperText>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        <FormControl variant="filled" fullWidth>
                            <AuthFormLabel error={errors?.email ? true : false}>
                                {t('LogIn.email')}
                            </AuthFormLabel>
                            <Controller
                                render={({ field }) => (
                                    <AuthFormInput
                                        {...field}
                                        defaultValue={signUpData.email}
                                        error={!!errors?.email}
                                    />
                                )}
                                control={control}
                                name="email"
                            />
                            <FormHelperText error={!!errors?.email}>
                                {fieldErrorMessage('email')}
                            </FormHelperText>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        <FormControl variant="filled" fullWidth>
                            <AuthFormLabel error={errors?.password ? true : false}>
                                {t('LogIn.password')}
                            </AuthFormLabel>
                            <Controller
                                render={({ field }) => (
                                    <AuthFormInput
                                        type={password ? 'text' : 'password'}
                                        {...field}
                                        error={!!errors?.password}
                                    />
                                )}
                                control={control}
                                name="password"
                            />
                            <img
                                style={{
                                    width: '25px',
                                    height: '25px',
                                    position: 'absolute',
                                    right: '15px',
                                    top: '33px',
                                    cursor: 'pointer',
                                }}
                                src={password ? visibilityOff : visibility}
                                onClick={showPassword}
                            />
                            <FormHelperText error={!!errors?.password}>
                                {fieldErrorMessage('password')}
                            </FormHelperText>
                        </FormControl>
                    </Grid>

                    {/* <Grid item xs={12} lg={6}>
              <FormControl variant="filled" fullWidth>
                <AuthFormLabel error={errors?.business_url ? true : false}>
                  {t("SignUp.website")}{" "}
                  <sup style={{ fontSize: "10px" }}>
                    {t("SignUp.websiteOptional")}
                  </sup>
                </AuthFormLabel>
                <Controller
                  render={({ field }) => (
                    <AuthFormInput
                      {...field}
                      {...register("business_url")}
                      error={errors?.business_url ? true : false}
                      placeholder={t("SignUp.websitePlaceholder")}
                    />
                  )}
                  control={control}
                  name="business_url"
                />
                <FormHelperText error={errors?.business_url ? true : false}>
                  {errors?.business_url ? t("SignUp.website") : ""}
                </FormHelperText>
              </FormControl>
            </Grid> */}
                    {/* {get(data, 'onboarding', Array(2).fill({onboardingOptions: []})).map(onboardingItem => (
                        <Grid item xs={12}>
                            <FormControl fullWidth variant="filled" key={onboardingItem?.id}>
                                <InputLabel id={'onboarding_' + onboardingItem?.id}>{t(onboardingItem?.question)}</InputLabel>
                                <Controller
                                    control={control}
                                    name={'onboarding_' + onboardingItem?.id}
                                    rules={{ required: true }}
                                    render={({ field }) => (
                                        <Select labelId={'onboarding_' + onboardingItem?.id} {...field}>
                                            {onboardingItem?.onboardingOptions?.map(option => (
                                                <MenuItem key={option?.id} value={option?.id}>{option?.option}</MenuItem>
                                            ))}
                                        </Select>
                                    )}>
                                </Controller>
                                {loading && <LinearProgress />}
                            </FormControl>
                        </Grid>
                    ))} */}
                    <Grid item xs={12} lg={6}>
                        <AuthFormLabel htmlFor="storeWhatsapp">
                            {t('SignUp.whatsapp')}
                        </AuthFormLabel>
                        <Grid container spacing={2} style={{ alignItems: 'center' }}>
                            <Grid item xs={3}>
                                <Controller
                                    control={control}
                                    name="storeCountry"
                                    rules={{ required: true }}
                                    render={({ field }) => (
                                        <Autocomplete
                                            variant="outlined"
                                            PopperComponent={PopperMy}
                                            id="storeWhatsappCountry"
                                            style={{ width: '100%' }}
                                            options={countries}
                                            size="small"
                                            value={countries.find((c) => c.code === field.value) || null}
                                            getOptionSelected={(o, v) => o?.code === v?.code}
                                            onChange={(event, value) => {
                                                field.onChange(value?.code ?? '');
                                                setValue('storeCountryNumber', value?.phone ?? '');
                                            }}
                                            onBlur={field.onBlur}
                                            name={field.name}
                                            disableClearable
                                            disableListWrap
                                            noOptionsText="No hay resultados"
                                            filterOptions={filterCountries}
                                            getOptionLabel={(option) => option?.phone || ''}
                                            renderOption={(option) => (
                                                <>
                                                    <span style={{ marginRight: '1rem' }}>
                                                        {countryToFlag(option.code)}
                                                    </span>
                                                    {`${option.label} +${option.phone}`}
                                                </>
                                            )}
                                            renderInput={(params) => (
                                                <SignUpFormTextField
                                                    {...params}
                                                    margin="normal"
                                                    placeholder="(+00)"
                                                    variant="outlined"
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        disableUnderline: false,
                                                        autoComplete: prefix, // disable browser autocomplete and autofill
                                                    }}
                                                />
                                            )}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={9}>
                                <Controller
                                    render={({ field }) => (
                                        <AuthFormInput
                                            {...field}
                                            error={errors?.store_phone ? true : false}
                                            fullWidth
                                            helperText={
                                                errors?.store_phone ? t('SignUp.phoneError') : ''
                                            }
                                            InputLabelProps={{ shrink: true }}
                                            margin="normal"
                                            type="tel"
                                            placeholder={t('SignUp.phonePlaceholder')}
                                        />
                                    )}
                                    control={control}
                                    name="store_phone"
                                    rules={{ required: true, pattern: phoneRegex }}
                                />
                            </Grid>
                        </Grid>
                        <FormHelperText error={errors?.store_phone ? true : false}>
                            {errors?.store_phone ? t('SignUp.phoneError') : ''}
                        </FormHelperText>
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        <AuthFormLabel htmlFor="storeWhatsapp">
                            {t('SignUp.phoneUser')}
                        </AuthFormLabel>
                        <Grid container spacing={2} style={{ alignItems: 'center' }}>
                            <Grid item xs={3}>
                                <Controller
                                    control={control}
                                    name="country"
                                    rules={{ required: true }}
                                    render={({ field }) => (
                                        <Autocomplete
                                            variant="outlined"
                                            PopperComponent={PopperMy}
                                            id="userPhoneCountry"
                                            style={{ width: '100%' }}
                                            options={countries}
                                            size="small"
                                            value={countries.find((c) => c.code === field.value) || null}
                                            getOptionSelected={(o, v) => o?.code === v?.code}
                                            onChange={(event, value) => {
                                                field.onChange(value?.code ?? '');
                                                setValue('countryNumber', value?.phone ?? '');
                                            }}
                                            onBlur={field.onBlur}
                                            name={field.name}
                                            disableClearable
                                            disableListWrap
                                            noOptionsText="No hay resultados"
                                            filterOptions={filterCountries}
                                            getOptionLabel={(option) => option?.phone || ''}
                                            renderOption={(option) => (
                                                <>
                                                    <span style={{ marginRight: '1rem' }}>
                                                        {countryToFlag(option.code)}
                                                    </span>
                                                    {`${option.label} +${option.phone}`}
                                                </>
                                            )}
                                            renderInput={(params) => (
                                                <SignUpFormTextField
                                                    {...params}
                                                    margin="normal"
                                                    placeholder="(+00)"
                                                    variant="outlined"
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        disableUnderline: false,
                                                        autoComplete: prefix, // disable browser autocomplete and autofill
                                                    }}
                                                />
                                            )}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={9}>
                                <Controller
                                    render={({ field }) => (
                                        <AuthFormInput
                                            {...field}
                                            error={errors?.user_phone ? true : false}
                                            fullWidth
                                            helperText={
                                                errors?.user_phone ? t('SignUp.phoneError') : ''
                                            }
                                            InputLabelProps={{ shrink: true }}
                                            margin="normal"
                                            type="tel"
                                            placeholder={t('SignUp.phonePlaceholder')}
                                        />
                                    )}
                                    control={control}
                                    name="user_phone"
                                    rules={{ required: true, pattern: phoneRegex }}
                                />
                            </Grid>
                        </Grid>
                        <FormHelperText error={errors?.user_phone ? true : false}>
                            {errors?.user_phone ? t('SignUp.phoneError') : ''}
                        </FormHelperText>
                    </Grid>
                </Grid>
                <SubmitButtonContainer lg={6}>
                    <AuthFormButton
                        disabled={saving}
                        type="submit"
                        size="medium"
                        variant="contained"
                        fullWidth
                    >
                        {saving ? (
                            <CircularProgress size={30} color="secondary" />
                        ) : (
                            t('SignUp.userFormCta')
                        )}
                    </AuthFormButton>
                </SubmitButtonContainer>
                <Typography variant="caption" align="center" component="div" style={{ color: colors.white }}>
                    Al registrarte, aceptas nuestras{' '}
                    <Link
                        href={legalUrls.terms}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: colors.white, textDecoration: 'underline' }}
                    >
                        Condiciones de Uso
                    </Link>{' '}
                    y{' '}
                    <Link
                        href={legalUrls.privacy}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: colors.white, textDecoration: 'underline' }}
                    >
                        Políticas de Privacidad
                    </Link>
                </Typography>
            </form>
        </>
    );
};

const mapStateToProps = ({ auth }) => ({
    isSigningUp: auth.isSigningUp,
    signUpData: auth.signUpData,
});

const mapDispatchToProps = (dispatch) =>
    bindActionCreators({ signUp, logIn, saveSignUpData, setShowWelcomePopup }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(SignUpUserInfoForm);
